import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { collection, query, where, getDocs, orderBy, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ORDER_STATUSES } from '../firebase/schema';
import { formatDate } from '../utils/dateFormatter';
import { FaFileInvoiceDollar, FaPencilAlt, FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import OrderView from '../components/OrderView';
import Invoice from '../components/Invoice';
import SearchInput from '../components/SearchInput';
import html2pdf from 'html2pdf.js';
import { useFacility } from '../context/FacilityContext';
import { filterByFacility } from '../utils/facilities';

const ArchiveContainer = styled.div`
  padding: 80px 1% 20px 1%;
  width: 98%;
  margin: 0 auto;
  background: var(--al-bg);
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  color: white;
  min-height: 100vh;
`;

const PageHeader = styled.h1`
  font-family: 'Montserrat', sans-serif;
  color: white;
  text-transform: uppercase;
  font-size: 24px;
  margin-bottom: 20px;
  font-weight: 600;
  letter-spacing: 0.05em;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const SearchWrapper = styled.div`
  width: 300px;
`;

const ResultCount = styled.span`
  font-size: 0.75em;
  color: rgba(255, 255, 255, 0.5);
  margin-left: auto;
`;

const ArchiveTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  font-family: 'Montserrat', sans-serif;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.8em;
  letter-spacing: 0.05em;
`;

const SortableTh = styled.th`
  text-align: left;
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.active ? 'var(--al-primary)' : 'rgba(255, 255, 255, 0.6)'};
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.8em;
  letter-spacing: 0.05em;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s;
  
  &:hover {
    color: var(--al-primary);
  }
  
  svg {
    margin-left: 6px;
    font-size: 0.9em;
    vertical-align: middle;
  }
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: white;
  text-transform: uppercase;
  font-size: 0.9em;
  letter-spacing: 0.02em;
`;

const TableRow = styled.tr`
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    cursor: pointer;
  }
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
  
  &.revise:hover {
    color: #FFB347;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  color: rgba(255, 255, 255, 0.6);
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--al-bg);
  border-radius: 4px;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  overflow: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h3`
  color: white;
  margin: 0;
  font-weight: 600;
  text-transform: uppercase;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ModalButton = styled.button`
  background: ${props => props.primary ? 'var(--al-primary)' : 'transparent'};
  color: ${props => props.primary ? '#222' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => props.primary ? 'var(--al-primary)' : 'rgba(255, 255, 255, 0.3)'};
  padding: 8px 16px;
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.85em;
  
  &:hover {
    background: ${props => props.primary ? '#7FFF7F' : 'rgba(255, 255, 255, 0.1)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.5em;
  cursor: pointer;
  padding: 4px 8px;
  
  &:hover {
    color: white;
  }
`;

const ConfirmModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
`;

const ConfirmContent = styled.div`
  background: var(--al-bg);
  padding: 30px;
  border-radius: 4px;
  width: 450px;
  
  h3 {
    color: white;
    margin-bottom: 16px;
    text-transform: uppercase;
    font-weight: 600;
  }
  
  p {
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 8px;
    text-transform: none;
  }
  
  .details {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.85em;
    margin-top: 16px;
    text-transform: none;
  }
  
  .buttons {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
  }
`;

const Archive = () => {
  const { facilityId } = useFacility();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Sorting state - default to newest first
  const [sortField, setSortField] = useState('completedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Invoice modal state
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const invoiceRef = useRef(null);
  
  // Revise order state
  const [reviseOrder, setReviseOrder] = useState(null);
  const [showReviseConfirm, setShowReviseConfirm] = useState(false);
  const [revising, setRevising] = useState(false);

  useEffect(() => {
    fetchArchivedOrders();
  }, [facilityId]);

  const fetchArchivedOrders = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'orders'),
        where('status', '==', ORDER_STATUSES.COMPLETED)
      );

      const querySnapshot = await getDocs(q);
      const ordersData = await Promise.all(
        querySnapshot.docs.map(async docSnap => {
          const orderData = { id: docSnap.id, ...docSnap.data() };
          
          // Fetch lines for this order
          const linesQuery = query(
            collection(db, 'orderLines'),
            where('orderId', '==', docSnap.id)
          );
          const linesSnapshot = await getDocs(linesQuery);
          const lines = linesSnapshot.docs.map(lineDoc => ({
            id: lineDoc.id,
            ...lineDoc.data()
          }));

          orderData.lines = lines;
          return orderData;
        })
      );

      setOrders(filterByFacility(ordersData, facilityId));
    } catch (error) {
      console.error('Error fetching archived orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter orders by search term
  const filteredOrders = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return orders.filter(order => (
      order.customerName?.toLowerCase().includes(searchLower) ||
      order.orderNumber?.toLowerCase().includes(searchLower) ||
      String(order.poNumber || '').toLowerCase().includes(searchLower) ||
      order.lines?.some(line => 
        line.designName?.toLowerCase().includes(searchLower) ||
        line.garmentColor?.toLowerCase().includes(searchLower)
      )
    ));
  }, [orders, searchTerm]);

  // Sort filtered orders
  const sortedOrders = useMemo(() => {
    return [...filteredOrders].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'poNumber':
          aVal = a.poNumber || '';
          bVal = b.poNumber || '';
          break;
        case 'dueDate':
          aVal = a.dueDate ? new Date(a.dueDate) : new Date(0);
          bVal = b.dueDate ? new Date(b.dueDate) : new Date(0);
          break;
        case 'customerName':
          aVal = a.customerName?.toLowerCase() || '';
          bVal = b.customerName?.toLowerCase() || '';
          break;
        case 'totalPieces':
          aVal = a.lines?.reduce((total, line) => total + (parseInt(line.totalQuantity) || 0), 0) || 0;
          bVal = b.lines?.reduce((total, line) => total + (parseInt(line.totalQuantity) || 0), 0) || 0;
          break;
        case 'completedAt':
        default:
          aVal = a.completedAt?.toDate?.() || a.completedAt || new Date(0);
          bVal = b.completedAt?.toDate?.() || b.completedAt || new Date(0);
          break;
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  }, [filteredOrders, sortField, sortDirection]);

  // Handle sort click
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort />;
    return sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  const handleViewOrder = async (order) => {
    try {
      const linesQuery = query(
        collection(db, 'orderLines'),
        where('orderId', '==', order.id),
        orderBy('lineNumber', 'asc')
      );
      
      const linesSnapshot = await getDocs(linesQuery);
      const lines = linesSnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      setSelectedOrder({
        ...order,
        lines: lines
      });
    } catch (error) {
      console.error('Error fetching orderLines:', error);
      setSelectedOrder({
        ...order,
        lines: []
      });
    }
  };

  // View invoice for an order
  const handleViewInvoice = async (order, e) => {
    e.stopPropagation();
    
    try {
      let orderWithEmail = { ...order };
      if (order.customerId && !order.contactEmail) {
        const customerDoc = await getDoc(doc(db, 'customers', order.customerId));
        if (customerDoc.exists()) {
          orderWithEmail.contactEmail = customerDoc.data().email;
        }
      }
      
      setInvoiceOrder(orderWithEmail);
      setShowInvoiceModal(true);
    } catch (error) {
      console.error('Error preparing invoice:', error);
      setInvoiceOrder(order);
      setShowInvoiceModal(true);
    }
  };

  // Email invoice to customer
  const handleEmailInvoice = async () => {
    if (!invoiceOrder?.contactEmail) {
      alert('No email address found for this customer');
      return;
    }
    
    setSendingEmail(true);
    try {
      if (!invoiceRef.current) {
        throw new Error('Invoice reference not available');
      }

      const element = invoiceRef.current;
      const opt = {
        margin: 0.5,
        filename: `Invoice_${invoiceOrder.poNumber || invoiceOrder.orderNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      const pdfBlob = await html2pdf().set(opt).from(element).output('blob');
      
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = opt.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      alert(`PDF downloaded. Please email to: ${invoiceOrder.contactEmail}`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF: ' + error.message);
    } finally {
      setSendingEmail(false);
    }
  };

  // Handle clicking Revise button
  const handleReviseClick = (order, e) => {
    e.stopPropagation();
    setReviseOrder(order);
    setShowReviseConfirm(true);
  };

  // Revise order - send back to Ready To Invoice
  const handleReviseOrder = async () => {
    if (!reviseOrder) return;
    
    setRevising(true);
    try {
      await updateDoc(doc(db, 'orders', reviseOrder.id), {
        status: ORDER_STATUSES.READY_TO_INVOICE,
        archived: false,
        updatedAt: new Date()
      });

      setOrders(prevOrders => prevOrders.filter(o => o.id !== reviseOrder.id));
      
      setShowReviseConfirm(false);
      setReviseOrder(null);
    } catch (error) {
      console.error('Error revising order:', error);
      alert('Error revising order: ' + error.message);
    } finally {
      setRevising(false);
    }
  };

  return (
    <ArchiveContainer>
      <HeaderContainer>
        <PageHeader>Order Archive</PageHeader>
      </HeaderContainer>

      <FiltersContainer>
        <SearchWrapper>
          <SearchInput
            placeholder="Search by customer, invoice #, design..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchWrapper>
        <ResultCount>
          {filteredOrders.length} archived order{filteredOrders.length !== 1 ? 's' : ''}
        </ResultCount>
      </FiltersContainer>

      {loading ? (
        <LoadingContainer>Loading archived orders...</LoadingContainer>
      ) : (
        <ArchiveTable>
          <thead>
            <tr>
              <SortableTh 
                active={sortField === 'poNumber'}
                onClick={() => handleSort('poNumber')}
              >
                Invoice # {getSortIcon('poNumber')}
              </SortableTh>
              <SortableTh 
                active={sortField === 'completedAt'}
                onClick={() => handleSort('completedAt')}
              >
                Completed {getSortIcon('completedAt')}
              </SortableTh>
              <SortableTh 
                active={sortField === 'dueDate'}
                onClick={() => handleSort('dueDate')}
              >
                Due Date {getSortIcon('dueDate')}
              </SortableTh>
              <SortableTh 
                active={sortField === 'customerName'}
                onClick={() => handleSort('customerName')}
              >
                Customer {getSortIcon('customerName')}
              </SortableTh>
              <Th>Designs</Th>
              <SortableTh 
                active={sortField === 'totalPieces'}
                onClick={() => handleSort('totalPieces')}
              >
                Pieces {getSortIcon('totalPieces')}
              </SortableTh>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {sortedOrders.map(order => (
              <TableRow 
                key={order.id}
                onClick={() => handleViewOrder(order)}
              >
                <Td style={{ fontWeight: 600 }}>{order.poNumber}</Td>
                <Td>
                  {order.completedAt 
                    ? formatDate(order.completedAt.toDate ? order.completedAt.toDate() : order.completedAt) 
                    : '-'}
                </Td>
                <Td>{formatDate(order.dueDate)}</Td>
                <Td>{order.customerName}</Td>
                <Td>
                  {order.lines && order.lines.length > 2 ? (
                    `(${order.lines.length} Lines)`
                  ) : (
                    order.lines?.slice(0, 2).map(line => (
                      <div key={line.id} style={{ marginBottom: '2px' }}>
                        {line.designName}
                      </div>
                    ))
                  )}
                </Td>
                <Td>
                  {order.lines?.reduce((total, line) => 
                    total + (parseInt(line.totalQuantity) || 0), 0).toLocaleString() || '0'}
                </Td>
                <Td>
                  <ActionButton
                    onClick={(e) => handleViewInvoice(order, e)}
                    title="View Invoice"
                  >
                    <FaFileInvoiceDollar />
                  </ActionButton>
                  <ActionButton
                    className="revise"
                    onClick={(e) => handleReviseClick(order, e)}
                    title="Revise Invoice"
                  >
                    <FaPencilAlt />
                  </ActionButton>
                </Td>
              </TableRow>
            ))}
          </tbody>
        </ArchiveTable>
      )}

      {selectedOrder && (
        <OrderView 
          order={selectedOrder} 
          onClose={() => setSelectedOrder(null)}
        />
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && (
        <ModalOverlay onClick={() => setShowInvoiceModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Invoice: {invoiceOrder?.poNumber}</ModalTitle>
              <ModalActions>
                <ModalButton 
                  primary 
                  onClick={handleEmailInvoice}
                  disabled={sendingEmail}
                >
                  {sendingEmail ? 'Generating...' : 'Download PDF'}
                </ModalButton>
                <CloseButton onClick={() => setShowInvoiceModal(false)}>×</CloseButton>
              </ModalActions>
            </ModalHeader>
            {invoiceOrder && (
              <Invoice
                order={invoiceOrder}
                invoiceRef={invoiceRef}
                onUpdate={() => {}}
              />
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Revise Order Confirmation Modal */}
      {showReviseConfirm && (
        <ConfirmModal onClick={() => !revising && setShowReviseConfirm(false)}>
          <ConfirmContent onClick={e => e.stopPropagation()}>
            <h3>Revise Invoice</h3>
            <p>
              Revise invoice for <strong>{reviseOrder?.customerName}</strong> (#{reviseOrder?.poNumber})?
            </p>
            <div className="details">
              This will:<br />
              • Move the order back to "Ready To Invoice" status<br />
              • Allow editing pricing and details<br />
              • Require re-completing the invoice when done
            </div>
            <div className="buttons">
              <ModalButton 
                onClick={() => setShowReviseConfirm(false)}
                disabled={revising}
              >
                Cancel
              </ModalButton>
              <ModalButton 
                primary
                onClick={handleReviseOrder}
                disabled={revising}
              >
                {revising ? 'Revising...' : 'Revise Invoice'}
              </ModalButton>
            </div>
          </ConfirmContent>
        </ConfirmModal>
      )}
    </ArchiveContainer>
  );
};

export default Archive;
